import i18n from '@/utils/i18n';
import {
  AuthService,
  ClientMetadata,
  CognitoUser,
} from '@domain/auth/blocs/services/auth-service';
import { theme as baseTheme } from '@/styles';
import { partnerSupplier } from '@/utils/environment';
import { useCallback } from 'react';
import { useTheme } from '@/contexts/theme-context';
import { VerificationMethod } from '@/enums';

export interface PunchoutParams {
  cookie: string;
  username: string;
}

/*
 * Custom auth flow lambda functions use accent color, name, and logo to
 * customize the verification code emails, and takes a verificationMethod to
 * know what channel to send the code to.
 * This function ensures we always send required fields.
 */
export const useSendChallengeAnswer = () => {
  const theme = useTheme();

  return useCallback(
    async (
      user: CognitoUser,
      answer: string,
      verificationMethod: VerificationMethod,
      translationsEnabled?: boolean,
    ) => {
      const locale = i18n.locale.replace('_', '-').split('-')[0];
      const clientMetadata: ClientMetadata = translationsEnabled
        ? {
            ...(verificationMethod === VerificationMethod.Email && {
              emailMessage: i18n.t('verification_code.email_message', {
                distributorName: partnerSupplier.fullName,
              }),
              emailSubject: i18n.t('verification_code.email_subject', {
                distributorName: partnerSupplier.fullName,
              }),
            }),
            ...((verificationMethod === VerificationMethod.SMS ||
              verificationMethod === VerificationMethod.Call) && { locale }),
          }
        : {};

      return await AuthService.sendChallengeAnswer(user, answer, {
        accentColor: theme.colors.accent,
        appName: partnerSupplier.fullName,
        supplierLogoURL: baseTheme.images.welcomeLogoUrl || '',
        verificationMethod,
        ...clientMetadata,
      });
    },
    [theme],
  );
};
